import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { QrCode, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import QRCode from 'react-qr-code';

import { API } from '../config/api';

export default function Checkin() {
  const { classId } = useParams();
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchQrData();
  }, [isAuthenticated, classId]);

  const fetchQrData = async () => {
    try {
      const response = await axios.get(`${API}/checkin/qr/${classId}`, { withCredentials: true });
      setQrData(response.data);
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualCheckin = async () => {
    setCheckingIn(true);
    try {
      await axios.post(`${API}/checkin/${classId}`, {}, { withCredentials: true });
      setCheckedIn(true);
      toast.success(t('checkinSuccess'));
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail });
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  if (!qrData) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <Card className="bg-white border-[#E9DDCC] rounded-2xl">
          <CardContent className="py-12">
            <QrCode className="w-12 h-12 text-[#D7CFC4] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#7A6455] mb-4">Aucune réservation trouvée pour ce cours</p>
            <Button onClick={() => navigate('/my-bookings')} className="btn-primary">
              {t('myBookings')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Button 
        variant="ghost" 
        onClick={() => navigate('/my-bookings')}
        className="mb-6 text-[#7A6455]"
      >
        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
        {t('back')}
      </Button>

      <Card className="bg-white border-[#E9DDCC] rounded-3xl overflow-hidden">
        <CardContent className="p-8">
          {checkedIn ? (
            <div className="text-center animate-fade-in" data-testid="checkin-success">
              <div className="w-20 h-20 rounded-full bg-[#8C9B86]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#8C9B86]" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl text-[#2C2A29] mb-2">
                {t('checkinSuccess')}
              </h2>
              <p className="text-[#7A6455]">Bienvenue, {user?.name} !</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="font-heading text-xl text-[#2C2A29] mb-2">
                  {t('qrCheckin')}
                </h2>
                <p className="text-sm text-[#7A6455]">{t('scanToCheckin')}</p>
              </div>

              <div className="qr-container mb-8" data-testid="qr-code">
                <QRCode 
                  value={JSON.stringify({
                    type: 'pilates_checkin',
                    class_id: qrData.class_id,
                    user_id: qrData.user_id,
                    booking_id: qrData.booking_id
                  })}
                  size={200}
                  bgColor="#FFFFFF"
                  fgColor="#2C2A29"
                  level="M"
                />
              </div>

              <div className="text-center mb-6">
                <p className="text-sm text-[#7A6455] mb-1">{qrData.user_name}</p>
                <p className="text-xs text-[#D7CFC4]">ID: {qrData.booking_id?.slice(-8)}</p>
              </div>

              <div className="border-t border-[#E9DDCC] pt-6">
                <p className="text-xs text-[#7A6455] text-center mb-4">
                  Ou pointez votre présence manuellement :
                </p>
                <Button 
                  onClick={handleManualCheckin}
                  disabled={checkingIn}
                  className="w-full btn-primary h-12"
                  data-testid="manual-checkin-btn"
                >
                  {checkingIn ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {t('checkIn')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
